// app/ has no slice, so no model/ to hold hooks — colocate the hook next to
// the component per spec §4.3 (docs/superpowers/specs/2026-07-30-separate-ui-logic-design.md).
import { useSupportStore } from '@/entities/support';
import { facilitySelection } from '@/entities/facility';
import { EMITTER_CONSTANTS } from '@/shared/config';
import {
  isWebViewCreateStudy,
  auth,
  addFreshChatWidget,
  SocketIo,
  CUSTOM_FRESHCHAT_ID_CLASSNAME,
} from '@/shared/lib';
import { useEmitter } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { t } from 'ttag';

/**
 * Supplies the authenticated layout with its navigation menu, support-chat launcher and banner flags. Joins the user's own socket room on mount, emits a room per support facility as they load and registers FreshChat with the user's profile outside the create-study webview, reloads the page when FACILITY_CHANGED arrives, and derives the marketing, logo and login banner flags from the auth state.
 *
 * @returns The navigation menu ref, the chat-widget opener and the banner visibility flags.
 */
export const useMain = () => {
  /**
   * Holds the list of support facilities read from the support store. Subscribes to the supportFacilities slice of useSupportStore so the layout reacts when the user's facilities load.
   *
   * @returns The supportFacilities array currently held in the support store.
   */
  const supportFacilitiesRedux = useSupportStore((s) => s.supportFacilities);

  const menu = useRef([
    {
      id: 0,
      name: `${t`Inbox`}`,
      link: '/inbox',
    },
    {
      id: 1,
      name: `${t`Studies`}`,
      link: '/studies',
    },
    {
      id: 3,
      name: `${t`Devices`}`,
      link: '/devices',
    },
    {
      id: 4,
      name: t`Fleet`,
      link: '/rma',
    },
    {
      id: 6,
      name: `${t`Support`}`,
      link: '/support',
    },
  ]);
  const listEmmitRoom = useRef<string[]>([]);

  /**
   * Reveals the FreshChat support widget from the custom launcher button. Sets the FreshChat frame element's visibility back to visible and calls fcWidget.open().
   *
   * @returns Nothing.
   */
  const openWidget = () => {
    const freshchatFrame = document.getElementById(CUSTOM_FRESHCHAT_ID_CLASSNAME.FRAME_ID);
    if (freshchatFrame) {
      freshchatFrame.style.visibility = 'visible';
    }
    (window as any).fcWidget.open();
  };

  useEffect(() => {
    if (auth.userId()) {
      (SocketIo as any).emitRoom(`user-${auth.userId()}`);
    }
  }, []);

  useEffect(() => {
    if ((supportFacilitiesRedux?.length ?? 0) > 0) {
      _.forEach(supportFacilitiesRedux, (supportFacility) => {
        if (!_.includes(listEmmitRoom.current, supportFacility?.id)) {
          listEmmitRoom.current.push(supportFacility?.id);
          (SocketIo as any).emitRoom(supportFacility?.id);
        }
      });
    }

    if ((supportFacilitiesRedux?.length ?? 0) > 0) {
      if (!isWebViewCreateStudy()) {
        const userInfo = {
          firstName: auth.firstName(),
          lastName: auth.lastName(),
          email: auth.email(),
          phone: auth.phone1(),
          userId: auth.userId(),
          facility: facilitySelection
            .userSupportFacilities()
            .map((supportFacility: any) => supportFacility.name)
            .join(', '),
        };
        addFreshChatWidget(userInfo);
      }
    }
  }, [supportFacilitiesRedux]);

  useEmitter(
    EMITTER_CONSTANTS.FACILITY_CHANGED,
    (msg: any) => {
      if (msg) {
        auth.setAdminOrSalesFacilityId(msg.newFacility);
        auth.setUserFacilityId(msg.newFacility);
        window.location.reload();
      }
    },
    [],
  );

  const hasMarketingBanner =
    !auth.isAdminOrSales() && !isWebViewCreateStudy() && !auth.hasLogoBanner();
  const hasLogoBanner = auth.hasLogoBanner();
  const hasLoginBanner = auth.isShowLoginByBanner();

  return { menu, openWidget, hasMarketingBanner, hasLogoBanner, hasLoginBanner };
};
